/**
 * List all the available feature flags
 */
const featureFlagList = [
  'bookOnBehalf',
  'enableSlackIntegration',
  'xero_integration',
  'budgetly_integration',
  'seat_selection',
  'reporting_agent',
  'agent_cancellation',
  'flight_change',
  'hotel_exchange',
  'agents_page',
  'vehicle_booking',
  'google_calendar_integration',
  'proactive_booking',
  'agent_flexible_date_strip',
  'agent_home',
] as const;

export type FeatureFlagValues = {
  [key in (typeof featureFlagList)[number]]: boolean;
};

/** Default values for feature flags */
export const featureFlagDefaultValues: FeatureFlagValues = {
  bookOnBehalf: true,
  // Only read where Statsig is off (development and e2e): staging and production resolve this gate
  // through Statsig, so this default cannot enable Slack for a real company.
  enableSlackIntegration: true,
  // Snake_case so this hook's lowercasing resolves to the same Statsig gate the backend asks for.
  xero_integration: true,
  budgetly_integration: true,
  // Snake_case to match the backend's Statsig gate of the same name (the hook lowercases the key for lookup).
  // Only read where Statsig is off (development and e2e), so seat selection is exercised while developing;
  // staging and production resolve this gate through Statsig, where it stays off until released.
  seat_selection: true,
  // Only read where Statsig is off (development and e2e), so the reporting agent is visible while
  // developing; staging and production resolve this gate through Statsig, where it stays off until released.
  reporting_agent: true,
  // Self-serve cancellation: the agent's cards, and the traveller cancelling their own trip from the
  // trip page rather than filing a request. Off by default so every environment keeps the request-only
  // flow until a company is enrolled in the beta, the same way the backend gate reads.
  agent_cancellation: false,
  // Self-serve flight change: the agent's leg picker and the cart's "Confirm change". Off by default so every
  // environment keeps the request-only flow until a company is enrolled, the same way the backend gate reads.
  flight_change: true,
  // Self-serve accommodation exchange: the trip page's "Change hotel" action and the CRM's, plus the agent's
  // cards. Off by default so every environment keeps the request-only flow until a company is enrolled, the
  // same way the backend gate reads.
  hotel_exchange: true,
  // Agents: the "Agents" menu entry and the (still empty) page behind it. Only read where Statsig is off
  // (development and e2e), so the entry is visible while it is being built; staging and production resolve
  // this gate through Statsig, where it stays off until there is something on the page to release.
  agents_page: true,
  // Rental cars: the "Cars" tab of the manual search (search and offer details, no booking yet). Snake_case to match
  // the backend's Statsig gate, which also refuses the queries when off. Only read where Statsig is off (development
  // and e2e), so the tab is visible while it is being built; staging and production resolve the gate through Statsig,
  // where it stays off until a company is enrolled.
  vehicle_booking: true,
  // Google Calendar on the profile's Integrations tab: Enable connects the user's own calendar instead of filing a
  // request. Snake_case to match the backend's Statsig gate, which also refuses the calendar queries when off. Only
  // read where Statsig is off (development and e2e); production resolves the gate through Statsig, where it stays off
  // until a company is enrolled. One gate per provider, so Outlook gets its own.
  google_calendar_integration: true,
  // The page an emailed plan link opens, which hands the plan to the agent. Snake_case to match the backend's Statsig
  // gate, which also refuses the open when off. Only read where Statsig is off (development and e2e); production
  // resolves the gate through Statsig, where it stays off until a company is enrolled.
  proactive_booking: true,
  // The fare strip in the agent chat only, so the agent can drop it while manual search keeps it (manual search and
  // the fetching of strip prices stay on the backend's flexible_date_strip). Only read where Statsig is off
  // (development and e2e), so the agent strip is visible while developing; staging and production resolve the gate
  // through Statsig.
  agent_flexible_date_strip: true,
  // The agent home page: a greeting, the composer, suggestion cards and the traveller's trips ready for review and
  // upcoming, shown in place of the agent's intro until the first message. UI only, no backend gate. Only read where
  // Statsig is off (development and e2e), so the page is visible while it is being built; production resolves the gate
  // through Statsig, where it stays off until a company is enrolled.
  agent_home: true,
};
